import "./NavigationDrawer.css";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Collapse } from "@mui/material";
import { AppDispatch, RootState } from "../../../redux/store";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../../redux/actions/user.actions";
import {
  HomeIcon,
  TubIcon,
  UsersIcon,
  DealershipIcon,
  CommentsIcon,
  LogsIcon,
  FirmwareIcon,
  CommandsIcon,
  RolesIcon,
  UserGroupIcon,
  PermissionsIcon,
  SettingIcon,
  LightModeIcon,
  DarkModeIcon,
  LogoutIcon,
  NewsIcon,
  PromoIcon,
  ApiKeyIcon,
  DownloadIcon,
  DownChevronIcon,
} from "../../../assets/svg/svg";
import permissionCheck from "../../Layout/permissionCheck";
import useRoleCheck from "../../../hooks/useRoleCheck";
import { localizeSpa } from "../../../utils/brand";

// A nav entry is either a leaf link (has `route`) or a collapsible group (has `children`).
type NavNode = {
  label: string;
  icon: React.FC;
  route?: string;
  permissions?: string[]; // visible if the user has ANY of these (reuses permissionCheck's `.some`)
  hideIf?: string[]; // hidden if the user has ANY of these (e.g. single Dealership when they can see all)
  children?: NavNode[];
};

const NavigationDrawer: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const handleLogout = () => {
    dispatch(logout());
  };

  const user = useSelector((state: RootState) => state.user.data);

  // Get impersonated dealership ID if available, otherwise use user's dealership ID
  const [dealershipId, setDealershipId] = useState<string | null>(null);

  useEffect(() => {
    const updateDealershipId = () => {
      const impersonatedDealershipId = sessionStorage.getItem("impersonate_dealership");
      setDealershipId(impersonatedDealershipId || user?.dealership_id);
    };

    // Set initial value
    updateDealershipId();

    // Listen for impersonation changes
    window.addEventListener('impersonate', updateDealershipId);
    window.addEventListener('impersonate_dealership_change', updateDealershipId);

    return () => {
      window.removeEventListener('impersonate', updateDealershipId);
      window.removeEventListener('impersonate_dealership_change', updateDealershipId);
    };
  }, [user?.dealership_id]);

  const [theme, setTheme] = useState<any>('light');
  var mode = localStorage.getItem('data-theme');
  useEffect(() => {
    if (mode) {
      setTheme(mode);
    } else if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      setTheme('dark');
    }
  }, [mode]);

  const handleChange = () => {
    if (theme === 'dark') {
      setTheme('light');
      document.body.setAttribute('data-theme', 'light');
      localStorage.setItem('data-theme', 'light');
    } else {
      setTheme('dark');
      document.body.setAttribute('data-theme', 'dark');
      localStorage.setItem('data-theme', 'dark');
    }
    window.dispatchEvent(new Event('storage'));
  };

  // Migration Progress is gated on the role rather than a permission, so it needs no
  // seeded permission row. Same impersonation-aware check RoleRoute runs on the routes.
  const { canAccess: isAdminOrDeveloper } = useRoleCheck(['admin', 'developer']);

  // Single source of truth for the nav. Built inside the component because a few entries
  // depend on runtime values (dealershipId, localized labels). Everyday links stay flat;
  // the technical and admin pages are consolidated into two collapsible groups.
  const navConfig: NavNode[] = useMemo(() => [
    { label: 'Home', route: '/', icon: HomeIcon },
    { label: localizeSpa('Spas'), route: '/spas', icon: TubIcon, permissions: ['view_spas'] },
    { label: 'Users', route: '/users', icon: UsersIcon, permissions: ['view_users'] },
    { label: 'Dealerships', route: '/dealerships', icon: DealershipIcon, permissions: ['view_dealerships'] },
    { label: 'Dealership', route: `/dealership/${dealershipId}`, icon: DealershipIcon, permissions: ['view_dealership'], hideIf: ['view_dealerships'] },
    { label: 'Comments', route: '/comments', icon: CommentsIcon, permissions: ['view_all_comments'] },
    { label: 'News', route: '/news', icon: NewsIcon },
    { label: 'Dealer Promos', route: '/dealer-promos', icon: PromoIcon, permissions: ['can_send_promos', 'can_send_promos_to_all', 'can_send_notifications', 'can_send_notifications_to_all'] },
    // Available to any user who can hold API keys, so it stays a top-level link rather than a dev tool.
    { label: 'My API Keys', route: '/my-api-keys', icon: ApiKeyIcon, permissions: ['manage_spa_api_keys', 'manage_all_api_keys'] },
    {
      label: 'Developer Tools',
      icon: CommandsIcon,
      children: [
        { label: 'Log History', route: '/logs', icon: LogsIcon, permissions: ['view_log_requests'] },
        { label: 'Manage Firmware', route: '/firmware', icon: FirmwareIcon, permissions: ['view_any_firmware'] },
        { label: localizeSpa('Spa Commands'), route: '/spa-commands', icon: CommandsIcon, permissions: ['view_spa_commands'] },
        { label: 'Manage API Keys', route: '/manage-api-keys', icon: ApiKeyIcon, permissions: ['manage_all_api_keys'] },
      ],
    },
    {
      label: 'Admin Panel',
      icon: RolesIcon,
      children: [
        { label: 'Roles', route: '/roles', icon: RolesIcon, permissions: ['view_roles'] },
        { label: 'User Groups', route: '/user-groups', icon: UserGroupIcon, permissions: ['view_user_groups'] },
        { label: 'Permissions', route: '/permissions', icon: PermissionsIcon, permissions: ['view_permissions'] },
        ...(isAdminOrDeveloper
          ? [{ label: 'Migration Progress', route: '/migration-progress', icon: DownloadIcon }]
          : []),
      ],
    },
    { label: 'Settings', route: '/settings', icon: SettingIcon },
  ], [dealershipId, isAdminOrDeveloper]);

  const isVisible = (node: NavNode): boolean => {
    // A group shows only when at least one of its children is visible, so empty groups vanish.
    if (node.children) return node.children.some(isVisible);
    if (node.permissions && !permissionCheck(user, node.permissions)) return false;
    if (node.hideIf && permissionCheck(user, node.hideIf)) return false;
    return true;
  };

  const isActive = (route?: string): boolean => {
    if (!route) return false;
    if (route === '/') return location.pathname === '/';
    return location.pathname === route || location.pathname.startsWith(route + '/');
  };

  const [openGroups, setOpenGroups] = useState<string[]>([]);

  const toggleGroup = (label: string) => {
    setOpenGroups(prev => prev.includes(label) ? prev.filter(l => l !== label) : [...prev, label]);
  };

  // Auto-expand (never auto-collapse) the group that owns the current route, so landing on
  // e.g. /firmware opens Developer Tools without stealing a group the user opened by hand.
  useEffect(() => {
    const group = navConfig.find(n => n.children?.some(c => isActive(c.route)));
    if (group) {
      setOpenGroups(prev => prev.includes(group.label) ? prev : [...prev, group.label]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, navConfig]);

  const renderNode = (node: NavNode): React.ReactNode => {
    if (!isVisible(node)) return null;
    const Icon = node.icon;

    if (node.children) {
      const open = openGroups.includes(node.label);
      return (
        <li className="nav-item nav-group" key={node.label}>
          <button
            className={`nav-group-toggle${open ? ' open' : ''}`}
            onClick={() => toggleGroup(node.label)}
            aria-expanded={open}
          >
            <span className="nav-icon"><Icon /></span>
            <span className="nav-text">{node.label}</span>
            <span className={`nav-chevron${open ? ' open' : ''}`}><DownChevronIcon /></span>
          </button>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <ul className="nav-sublist">
              {node.children.map(renderNode)}
            </ul>
          </Collapse>
        </li>
      );
    }

    return (
      <li className="nav-item" key={node.label}>
        <a
          className={`nav-button${isActive(node.route) ? ' active' : ''}`}
          href={node.route}
          onClick={e => {
            e.preventDefault();
            if (node.route) navigate(node.route);
          }}
        >
          <span className="nav-icon"><Icon /></span>
          <span className="nav-text">{node.label}</span>
        </a>
      </li>
    );
  };

  return (
    <ul className="nav-list">
      <li className="nav-item">
        <button className="nav-button nav-profile">
          <span className="nav-profile-pic">{user?.name ? user.name[0] : `U`}</span>
          <span className="nav-text">Welcome, {user?.username ?? 'user'}.</span>
        </button>
      </li>

      {navConfig.map(renderNode)}

      <li className="nav-item nav-spacer" />

      <li className="nav-item">
        <button className="nav-button" onClick={() => handleChange()}>
          <span className="nav-icon">
            {theme === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
          </span>
          <span className="nav-text">{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
        </button>
      </li>

      <li className="nav-item">
        <button className="nav-button logout-button" onClick={() => handleLogout()}>
          <span className="nav-icon"><LogoutIcon /></span>
          <span className="nav-text">Logout</span>
        </button>
      </li>
    </ul>
  );
};

export default NavigationDrawer;
